import type { TradeCycleVersion } from '@festina/unitlink-general-types/unitlink'
import { dateUtils, notEmpty } from '@festina-life/core'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { Card, CardBody, CardHeader } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { TradeCycleEventsTable } from './TradeCycleEventsTable'
import { TradeOrderStatusTable } from './TradeOrderStatusTable'

type Props = {
  tradeCycle: TradeCycleVersion
  nextTradeCycle: TradeCycleVersion | undefined
}

export const TradeCycleCard = ({ tradeCycle, nextTradeCycle }: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()

  const tradeOrderStatuses = useMemo(
    () => tradeCycle.status?.tradeOrderStatuses?.filter(notEmpty) ?? [],
    [tradeCycle],
  )

  const tradeCycleEvents = useMemo(
    () =>
      tradeCycle.events
        ?.filter(notEmpty)
        // "The list of events, new events gets added to the end."
        // We want the most recent even first, so the list should be reversed.
        .reverse() ?? [],
    [tradeCycle],
  )

  const startDate = tradeCycle.fromTime
  const endDate = nextTradeCycle?.fromTime

  const dateRange = endDate
    ? `${dateUtils.dateWithTimeAsStr(startDate)} - ${dateUtils.dateWithTimeAsStr(endDate)}`
    : dateUtils.dateWithTimeAsStr(startDate)
  const cycleType = tradeCycle.tradeCycleType
    ? ` - ${translateEnum('unitLink.enum.TradeCycleType', tradeCycle.tradeCycleType)}`
    : ''
  const title = `${translate('tradeCycle.cycle.events')} - ${translate('tradeCycle.cycle.title')}: ${dateRange}${cycleType}`

  return (
    <Card>
      <CardHeader title={title} />
      <CardBody>
        {!!tradeOrderStatuses.length && (
          <TradeOrderStatusTable tradeOrderStatuses={tradeOrderStatuses} />
        )}
        {!!tradeCycleEvents.length && (
          <TradeCycleEventsTable tradeCycleEvents={tradeCycleEvents} />
        )}
      </CardBody>
    </Card>
  )
}
